/**
 * Catalogo autorizado: o que ESTE usuario alcanca, por workspace.
 *
 * O backend ja devolve so o permitido - ele cruza o catalogo do Report Manager com o que o Auth
 * autoriza. A tela NAO filtra por permissao: se filtrasse, haveria dois lugares decidindo acesso, e
 * um deles acabaria desatualizado.
 *
 * O workspace escolhido vem da navegacao (casca); a busca e daqui, ao lado da contagem - no lugar
 * em que o Auth Manager poe a busca do diretorio.
 */

import { Button, Card, StatusBadge } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { listReports } from '../api/reports'
import type { ReportListItem } from '../api/types'
import { Alert } from '../components/Alert'
import { Skeleton } from '../components/Skeleton'
import { PageHeader } from '../shell/AppShell'
import { grupoDe, TODOS } from './workspaces'

export function CatalogPage({ workspace, onOpen }: { workspace: string; onOpen: (reportId: string) => void }) {
  const [busca, setBusca] = useState('')
  const relatorios = useQuery({ queryKey: ['reports'], queryFn: listReports })

  const doWorkspace = (relatorios.data ?? []).filter(
    (item) => workspace === TODOS || grupoDe(item) === workspace,
  )
  const termo = busca.trim().toLowerCase()
  const visiveis = doWorkspace.filter((item) => {
    if (termo === '') return true
    const alvo = item.name + ' ' + (item.description ?? '') + ' ' + (item.category ?? '')
    return alvo.toLowerCase().includes(termo)
  })

  const contagem =
    termo === ''
      ? `${doWorkspace.length} ${doWorkspace.length === 1 ? 'relatório' : 'relatórios'}`
      : `${visiveis.length} de ${doWorkspace.length}`

  return (
    <>
      <PageHeader
        descricao={
          workspace === TODOS
            ? 'Os relatórios que o seu perfil alcança, de todos os workspaces.'
            : 'Os relatórios deste workspace que o seu perfil alcança.'
        }
        sobretitulo={workspace === TODOS ? 'Catálogo' : 'Workspace'}
        titulo={workspace === TODOS ? 'Meus relatórios' : workspace}
      />

      <div className="catalogo__barra">
        <p className="catalogo__contagem">{relatorios.isPending ? 'Carregando…' : contagem}</p>
        <input
          aria-label="Buscar relatório"
          className="catalogo__busca"
          onChange={(event) => setBusca(event.target.value)}
          placeholder="Buscar relatório…"
          type="search"
          value={busca}
        />
      </div>

      {relatorios.isPending ? (
        <div className="catalog-grid">
          {[0, 1, 2, 3, 4, 5].map((indice) => (
            <Skeleton key={indice} height={156} />
          ))}
        </div>
      ) : relatorios.error !== null ? (
        <Alert
          tone="danger"
          action={<Button onClick={() => void relatorios.refetch()}>Tentar de novo</Button>}
        >
          Não foi possível carregar o catálogo: {relatorios.error.message}
        </Alert>
      ) : visiveis.length === 0 ? (
        <div className="state">
          <p>
            {(relatorios.data ?? []).length === 0
              ? 'Você ainda não tem relatórios liberados. Fale com quem administra o catálogo.'
              : 'Nenhum relatório corresponde à busca.'}
          </p>
        </div>
      ) : (
        <div className="catalog-grid">
          {visiveis.map((item) => (
            <ReportCard item={item} key={item.id} onOpen={() => onOpen(item.id)} />
          ))}
        </div>
      )}
    </>
  )
}

function ReportCard({ item, onOpen }: { item: ReportListItem; onOpen: () => void }) {
  const descricao = (item.description ?? '').trim()
  return (
    <Card className="report-card">
      <h3 className="report-card__name">{item.name}</h3>
      <p className="report-card__description">{descricao === '' ? 'Sem descrição.' : descricao}</p>
      <div className="report-card__meta">
        <StatusBadge tone={item.report_type === 'inteli_graph' ? 'tech' : 'neutral'}>
          {item.report_type === 'inteli_graph' ? 'Inteli Graph' : 'Power BI'}
        </StatusBadge>
        {item.category === null || item.category.trim() === '' ? null : (
          <span className="report-card__category">{item.category}</span>
        )}
      </div>
      <Button variant="primary" size="small" onClick={onOpen}>
        Abrir
      </Button>
    </Card>
  )
}
